import React, { useRef } from 'react';
import { motion, useAnimationFrame } from 'framer-motion';
import { useDb } from '../../../hooks/useDb';
import { useMotionPreference } from '../../../hooks/useMotionPreference';
import { activeTasks } from '../../../utils/dbSelectors';
import { getAgent } from '../../../utils/agents';
import type { AgentId } from '../../../types/anna';
interface SatellitesProps {
  cx: number;
  cy: number;
  R: number;
  stopped: boolean;
  /** Niet storen: still moving, but quieter */
  dim: boolean;
  flash: {
    agent: AgentId;
    id: number;
  } | null;
  openAgent: AgentId | null;
  onOpen: (agent: AgentId, x: number, y: number) => void;
}
const TAU = Math.PI * 2;
const TILT = -0.14;
const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * One satellite per active agent, in its colour, orbiting Anna. Size = activity; speed = the task's pace.
 * Waiting agents barely drift and show as a ring. Tap to unfold the live thread.
 */
export function Satellites({
  cx,
  cy,
  R,
  stopped,
  dim,
  flash,
  openAgent,
  onOpen
}: SatellitesProps) {
  const reduce = useMotionPreference();
  const tasks = useDb(activeTasks);
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const angles = useRef<Record<string, number>>({});
  const pos = useRef<Record<string, {
    x: number;
    y: number;
  }>>({});
  const frozen = stopped || reduce;
  useAnimationFrame((_, delta) => {
    const dt = Math.min(delta, 50) / 1000;
    tasks.forEach((task, i) => {
      const el = refs.current[task.id];
      if (!el) return;
      let a = angles.current[task.id] ?? i / Math.max(1, tasks.length) * TAU + 0.6;
      if (!frozen && openAgent !== task.agent) a += dt * (task.status === 'bezig' ? 0.05 + task.pace * 0.1 : 0.015);
      angles.current[task.id] = a;
      const rx = R * (1.08 + i % 3 * 0.08);
      const ry = rx * 0.3;
      const ex = rx * Math.cos(a);
      const ey = ry * Math.sin(a);
      const x = cx + ex * Math.cos(TILT) - ey * Math.sin(TILT);
      const y = cy + ex * Math.sin(TILT) + ey * Math.cos(TILT);
      pos.current[task.id] = {
        x,
        y
      };
      // Lower half of the ellipse = in front of Anna: larger and brighter.
      const k = 0.5 + 0.5 * Math.sin(a);
      el.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%) scale(${(0.72 + 0.36 * k).toFixed(3)})`;
      el.style.opacity = ((dim ? 0.6 : 1) * (0.5 + 0.5 * k)).toFixed(3);
    });
  });
  return <div className="pointer-events-none absolute inset-0 z-[3]" data-id="element-1500" data-mp="1eea99jqqzaeh">
      {tasks.map(task => {
      const a = getAgent(task.agent);
      const waiting = task.status === 'wacht';
      const size = waiting ? 18 : Math.round(12 + task.pace * 18);
      const color = stopped ? '#6B7480' : a.color;
      return <button key={task.id} ref={el => {
        refs.current[task.id] = el;
      }} type="button" data-satellite onClick={() => {
        const p = pos.current[task.id] ?? {
          x: cx,
          y: cy
        };
        onOpen(task.agent, p.x, p.y);
      }} aria-expanded={openAgent === task.agent} aria-label={`${a.name}: ${task.title}, ${waiting ? 'wacht op jou' : `stap ${task.step} van ${task.totalSteps}`}. Tik voor details.`} className="pointer-events-auto absolute left-0 top-0 grid h-12 w-12 place-items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70" style={{
        transform: `translate(${cx}px, ${cy}px) translate(-50%, -50%)`
      }} data-id="element-1501" data-mp="1j6tosm8zwflx">
            <span aria-hidden className="block rounded-full" style={{
          width: size,
          height: size,
          backgroundColor: waiting ? 'transparent' : color,
          border: waiting ? `2px solid ${color}` : undefined,
          boxShadow: stopped ? 'none' : `0 0 ${size}px ${a.color}66`
        }} data-id="element-1502" data-mp="1xtimcijosipo" />
            {flash?.agent === task.agent && <motion.span key={flash.id} aria-hidden className="absolute rounded-full border-2" style={{
          width: size,
          height: size,
          borderColor: a.color
        }} initial={{
          scale: 1,
          opacity: 0.9
        }} animate={{
          scale: 3,
          opacity: 0
        }} transition={{
          duration: 0.6,
          ease: EASE
        }} data-id="element-1503" data-mp="2hfdxj3d9hj27" />}
          </button>;
    })}
    </div>;
}